import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Video, MapPin, Lock, MessageCircle } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { waLink, WA_MESSAGES } from "../lib/whatsapp";

const IMG_PORTRAIT = "/dr-tiago-1.jpg";
const IMG_MEETING = "/dr-tiago-2.jpg";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-20, 30]);

  return (
    <section id="sobre" ref={ref} data-testid="about-section" className="relative py-24 sm:py-32 bg-[#0E1728] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div className="relative order-2 lg:order-1">
            <motion.div style={{ y: y1 }} className="relative z-10 w-[78%]">
              <div className="rounded-2xl overflow-hidden border border-amber-500/25 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
                <img
                  src={IMG_PORTRAIT}
                  alt="Dr. Tiago Santo, sócio-fundador da TSS — ADVOGADOS"
                  className="w-full aspect-[4/5] object-cover object-[50%_22%]"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <p className="font-display text-base font-semibold text-slate-100">Dr. Tiago Santo</p>
                  <p className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-amber-400/90 mt-1">
                    OAB/MS 26.983 · CEO
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div style={{ y: y2 }} className="absolute right-0 bottom-[-8%] w-[52%] z-20">
              <div className="rounded-2xl overflow-hidden border border-amber-400/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                <img
                  src={IMG_MEETING}
                  alt="Dr. Tiago Santo em retrato editorial"
                  className="w-full aspect-[4/3] object-cover object-[50%_18%]"
                  loading="lazy"
                />
              </div>
            </motion.div>
            <div className="absolute -left-8 top-1/3 w-40 h-40 rounded-full bg-amber-400/10 blur-3xl" />
          </div>

          <div className="order-1 lg:order-2">
            <SectionHeading
              eyebrow="Sobre a TSS — Advogados"
              title="Estratégia, sigilo e presença, onde quer que você esteja"
            />
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mt-7">
                O fim de um relacionamento não precisa custar a sua paz, nem o patrimônio que você
                construiu. O divórcio envolve muito mais do que o fim de uma relação.
              </p>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mt-4">
                A divisão dos bens, a guarda dos filhos, a pensão alimentícia e a regulamentação da
                convivência familiar exigem decisões importantes, que podem impactar diretamente o
                seu futuro e o de quem você ama.
              </p>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mt-4">
                Na <span className="text-slate-200 font-semibold">TSS – ADVOGADOS</span>, sob a
                liderança do <span className="text-amber-200">Dr. Tiago Santo (OAB/MS 26.983)</span>,
                você conta com assessoria jurídica estratégica e acompanhamento de ponta a ponta para
                conduzir o divórcio e resolver as questões familiares e patrimoniais com segurança,
                clareza e responsabilidade.
              </p>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mt-4">
                Nosso objetivo é proteger seus direitos, buscar soluções juridicamente adequadas e
                reduzir o desgaste de um momento que já é delicado.{" "}
                <span className="text-amber-200 font-semibold">
                  Menos conflitos. Mais segurança jurídica. Tranquilidade para recomeçar.
                </span>
              </p>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed mt-4">
                Atendemos clientes em <span className="text-amber-200">todo o Brasil</span> por
                videochamada criptografada, com a mesma profundidade de um atendimento presencial,
                e mantemos estrutura física em <span className="text-amber-200">Naviraí/MS</span>{" "}
                para quem prefere olho no olho.
              </p>
            </Reveal>

            <div className="mt-9 space-y-4" data-testid="about-badges">
              {[
                { icon: Video, title: "100% digital em todo o Brasil", text: "Consultas por videochamada segura, documentos assinados eletronicamente." },
                { icon: MapPin, title: "Escritório em Naviraí/MS", text: "Atendimento presencial com hora marcada e total discrição." },
                { icon: Lock, title: "Sigilo absoluto garantido", text: "Nenhuma informação sua é compartilhada. Nunca." },
              ].map((b, i) => (
                <Reveal key={b.title} delay={0.15 + i * 0.07}>
                  <div className="flex items-start gap-4 border border-amber-500/15 bg-[#111C2E] rounded-xl px-5 py-4">
                    <b.icon className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-slate-100">{b.title}</p>
                      <p className="text-xs text-slate-400 mt-1">{b.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.3}>
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href={waLink(WA_MESSAGES.about)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-cta-whatsapp"
                className="inline-flex items-center gap-2.5 border border-amber-400/40 text-amber-200 font-semibold text-sm px-7 py-3.5 rounded-full mt-9 hover:bg-amber-400/10 hover:border-amber-300 transition-colors duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Agendar Conversa Confidencial
              </motion.a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
